import * as rxjs from "rxjs";
import type {
  ActionHandlers,
  ActionKeys,
  ActionMap,
  DispatchedAction,
  DispatchError,
  DispatchEvent,
  DispatchMode,
  DispatchOptions,
  Message,
  MessageOfType,
  SpanContext,
  SpanEndStatus,
  SpanOptions,
  SpanRef,
  TraceId,
} from "@/types/dispatch.types.tsx";
import { reduce } from "@/core/dispatch.reducer.tsx";
import { newSpanId, newTraceId } from "@/core/dispatch.ids.tsx";
import { type ClockOptions, DispatchClock, type TickInfo } from "@/core/dispatch.clock.tsx";

export interface DispatcherOptions {
  mode?: DispatchMode;
  clock?: ClockOptions;
}

export interface TickBatch<A extends ActionMap> {
  ts: TickInfo;
  list: DispatchedAction<A>[];
}

interface Entry<A extends ActionMap> {
  span: SpanRef;
  message: Message<A>;
  taken: boolean;
}

interface Step<A extends ActionMap, S> {
  state: S;
  entry: Entry<A>;
  failed: boolean;
  error?: unknown;
}

function isPromiseLike(value: unknown): value is PromiseLike<unknown> {
  return typeof (value as { then?: unknown } | null)?.then === "function";
}

export abstract class Dispatcher<A extends ActionMap, S, P = undefined> {
  protected abstract readonly handlers: ActionHandlers<A, S>;

  readonly #release$ = new rxjs.Subject<void>();
  readonly #events$ = new rxjs.Subject<DispatchEvent>();
  readonly #error$ = new rxjs.Subject<DispatchError<A>>();
  readonly #subs = new rxjs.Subscription();
  #pending: Entry<A>[] = [];
  #current: SpanRef | null = null;
  #tickSub: rxjs.Subscription | null = null;
  #mode: DispatchMode;
  #state: S;
  #eventSeq = 0;
  #disposed = false;

  readonly clock: DispatchClock;
  readonly tick$: rxjs.Observable<TickInfo>;
  readonly action$: rxjs.Observable<DispatchedAction<A>>;
  readonly dispatch$: rxjs.Observable<Message<A>>;
  readonly state$: rxjs.Observable<S>;
  readonly events$: rxjs.Observable<DispatchEvent> = this.#events$.asObservable();
  readonly error$: rxjs.Observable<DispatchError<A>> = this.#error$.asObservable();

  constructor(readonly props: P, initialState: S, options: DispatcherOptions = {}) {
    this.#state = initialState;
    this.#mode = options.mode ?? "immediate";

    this.clock = new DispatchClock(options.clock);
    this.tick$ = this.clock.tick$;

    const steps$ = this.#release$.pipe(
      rxjs.concatMap(() => rxjs.from(this.#take())),
      rxjs.scan(
        (acc, entry) => this.#step(acc.state, entry),
        { state: initialState } as Step<A, S>,
      ),
      rxjs.share(),
    );

    this.state$ = steps$.pipe(
      rxjs.map((step) => step.state),
      rxjs.startWith(initialState),
      rxjs.distinctUntilChanged(),
      rxjs.shareReplay({ bufferSize: 1, refCount: false }),
    );
    this.action$ = steps$.pipe(
      rxjs.map(({ entry }): DispatchedAction<A> => ({ ...entry.span, message: entry.message })),
    );
    this.dispatch$ = this.action$.pipe(rxjs.map((action) => action.message));

    this.#subs.add(this.state$.subscribe((state) => {
      this.#state = state;
    }));
    this.#subs.add(steps$.subscribe((step) => this.#settle(step)));
    this.#subs.add(this.clock.change$.subscribe((change) =>
      this.#emit({
        ...this.#stamp(),
        kind: `clock/${change.kind}`,
        interval: change.interval,
      })
    ));
    this.#syncMode();
  }

  get state(): S {
    return this.#state;
  }

  get disposed(): boolean {
    return this.#disposed;
  }

  get mode(): DispatchMode {
    return this.#mode;
  }

  get queueLength(): number {
    return this.#pending.length;
  }

  getState(): S {
    return this.#state;
  }

  dispatch(message: Message<A>, options: DispatchOptions = {}): TraceId {
    const span = this.#newSpan(options.parent);
    if (this.#disposed) return span.traceId;

    this.#pending.push({ span, message, taken: false });
    this.#emit({
      ...this.#stamp(),
      ...span,
      kind: "span/start",
      spanKind: "dispatch",
      name: message.type,
      attributes: "payload" in message ? { payload: message.payload } : {},
    });
    if (this.#mode === "immediate") this.#release$.next();
    return span.traceId;
  }

  span<T>(name: string, work: (span: SpanContext<A>) => T, options: SpanOptions = {}): T {
    const span = this.#newSpan(options.parent);
    this.#emit({
      ...this.#stamp(),
      ...span,
      kind: "span/start",
      spanKind: "span",
      name,
      attributes: options.attributes ?? {},
    });

    const previous = this.#current;
    this.#current = span;
    let result: T;
    try {
      result = work(this.#context(span));
    } catch (error) {
      this.#end(span, "error", error);
      throw error;
    } finally {
      this.#current = previous;
    }

    if (!isPromiseLike(result)) {
      this.#end(span, "ok");
      return result;
    }
    return result.then(
      (value) => {
        this.#end(span, "ok");
        return value;
      },
      (error) => {
        this.#end(span, "error", error);
        throw error;
      },
    ) as T;
  }

  flush(): void {
    if (!this.#disposed) this.#release$.next();
  }

  setMode(mode: DispatchMode): void {
    if (this.#disposed || mode === this.#mode) return;
    this.#mode = mode;
    this.#emit({ ...this.#stamp(), kind: "mode", mode });
    this.#syncMode();
  }

  select$<T>(selector: (state: S) => T, isEqual: (a: T, b: T) => boolean = Object.is) {
    return this.state$.pipe(rxjs.map(selector), rxjs.distinctUntilChanged(isEqual));
  }

  ofType$<K extends ActionKeys<A>>(...types: K[]): rxjs.Observable<MessageOfType<A, K>> {
    return this.dispatch$.pipe(
      rxjs.filter((message): message is MessageOfType<A, K> =>
        (types as string[]).includes(message.type)
      ),
    );
  }

  sync$<T>(source$: rxjs.Observable<T>): rxjs.Observable<T> {
    return source$.pipe(rxjs.sample(this.tick$));
  }

  tickBatch$(): rxjs.Observable<TickBatch<A>> {
    return new rxjs.Observable<TickBatch<A>>((subscriber) => {
      let list: DispatchedAction<A>[] = [];
      const sub = new rxjs.Subscription();
      sub.add(this.action$.subscribe((action) => list.push(action)));
      sub.add(this.tick$.subscribe((ts) => {
        const batch = list;
        list = [];
        subscriber.next({ ts, list: batch });
      }));
      return sub;
    });
  }

  dispatchFrom(source$: rxjs.Observable<Message<A>>): rxjs.Subscription {
    const sub = source$.subscribe((message) => this.dispatch(message));
    this.#subs.add(sub);
    return sub;
  }

  dispose(): void {
    if (this.#disposed) return;
    this.#disposed = true;

    this.#tickSub?.unsubscribe();
    this.#tickSub = null;

    const dropped = this.#pending;
    this.#pending = [];
    for (const entry of dropped) this.#end(entry.span, "dropped");

    this.clock.dispose();
    this.#release$.complete();
    this.#subs.unsubscribe();
    this.#events$.complete();
    this.#error$.complete();
  }

  #syncMode() {
    this.#tickSub?.unsubscribe();
    this.#tickSub = null;

    if (this.#mode === "tick") {
      this.#tickSub = this.tick$.subscribe(() => this.#release$.next());
    } else {
      this.#release$.next();
    }
  }

  #newSpan(parent: SpanRef | null | undefined): SpanRef {
    const p = parent === undefined ? this.#current : parent;
    return {
      traceId: p?.traceId ?? newTraceId(),
      spanId: newSpanId(),
      parentId: p?.spanId ?? null,
    };
  }

  #context(ref: SpanRef): SpanContext<A> {
    return {
      ...ref,
      dispatch: (message) => this.dispatch(message, { parent: ref }),
      span: (name, work, options) => this.span(name, work, { ...options, parent: ref }),
    };
  }

  #take(): Entry<A>[] {
    const batch = this.#pending.filter((entry) => !entry.taken);
    for (const entry of batch) entry.taken = true;

    if (this.#mode === "tick" && batch.length > 0) {
      this.#emit({ ...this.#stamp(), kind: "flush", size: batch.length });
    }
    return batch;
  }

  #step(state: S, entry: Entry<A>): Step<A, S> {
    this.#emit({ ...this.#stamp(), ...entry.span, kind: "span/mark", name: "processing" });

    const previous = this.#current;
    this.#current = entry.span;
    try {
      const next = reduce(this.handlers, state, entry.message, this.#context(entry.span));
      return { state: next, entry, failed: false };
    } catch (error) {
      return { state, entry, failed: true, error };
    } finally {
      this.#current = previous;
    }
  }

  #settle({ entry, failed, error }: Step<A, S>) {
    const index = this.#pending.indexOf(entry);
    if (index >= 0) this.#pending.splice(index, 1);

    this.#end(entry.span, failed ? "error" : "ok", error);
    if (!failed) return;

    const { traceId, spanId } = entry.span;
    if (this.#error$.observed) {
      this.#error$.next({ traceId, spanId, message: entry.message, error });
    } else {setTimeout(() => {
        throw error;
      });}
  }

  #end(span: SpanRef, status: SpanEndStatus, error?: unknown) {
    this.#emit({
      ...this.#stamp(),
      ...span,
      kind: "span/end",
      status,
      ...(error !== undefined ? { error } : {}),
    });
  }

  #stamp() {
    return {
      seq: ++this.#eventSeq,
      tick: this.clock.tick,
      t: this.clock.now(),
      queue: this.#pending.length,
    };
  }

  #emit(event: DispatchEvent) {
    this.#events$.next(event);
  }
}
